/**
 *  @author: Maximilian Gertz
 *  @email: mgertz@nanocosmos.net
 *  @organization: Nanocosmos GmbH
 *  @version: (introduced in analytics-front) 2.26.0
 */

// React Imports
import React, { useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { CardTitle, Col, Row } from 'reactstrap';
import ReactDOMServer from 'react-dom/server';
import { Tooltip as ReactTooltip } from 'react-tooltip/dist/react-tooltip.umd';

// Redux Imports
import { useSelector } from 'react-redux';

// External Libraries
import moment from 'moment';

// Custom Imports
import {
   AlignedIconContainer,
   HeightSetContainer,
   PaddingContainer,
   SimpleFlexItem,
   SimpleFlexRow,
   WidgetBodyContainer,
   WidgetContainer,
   WidgetHeaderContainer,
   StandardContainer,
   Description,
   ValueText,
   CenteredMsgShowing,
   TwoSideFlexContainer,
   BottomMargin,
} from '../../../../../../styledComponents/styledComponents';
import { ALERT_MAPPER, getPrimaryAlertCode } from './AlertDetailsMapper';
import { CsvExportSplitApiButton } from '../../../../../common';
import BlockSpinner from '../../../../../common/BlockSpinner/BlockSpinner';
import { useAlertDetails } from './useAlertDetails';
import { CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED, CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT } from '../../../constants';
import { cancelTokenSelector, hashSelector, troubleModeSelector } from '../../../../../../redux/selectors/selectors';
import { getHttpHeader } from '../../../../../../util/FetchTools';
import { DetailTile, TileContainer } from '../../../Styles';
import TextWithToolTip from '../../../TextWithToolTip';
import { E2E } from '../../../../../../constants/general';
import AlertDetailsPanel from './AlertDetailsPanel';

const RESTART_DETAILS = [
   {
      name: 'Restart Count',
      propPath: 'custom.total',
   },
   {
      name: 'Analyzed Events',
      propPath: 'analyzedEvents',
      description: 'the total number of analyzed status events',
   },
   {
      name: 'Min Distance (seconds)',
      propPath: 'custom.distances.minDist',
      description: 'the smallest time interval between 2 restarts in seconds',
   },
   {
      name: 'Max Distance (seconds)',
      propPath: 'custom.distances.maxDist',
      description: 'the largest time interval between 2 restarts in seconds',
   },
   {
      name: 'Avg Distance (seconds)',
      propPath: 'custom.distances.avgDist',
      description: 'the average time interval between 2 restarts in seconds',
   },
];

function getValueFromPath(obj, path) {
   const pathParts = path.split('.');
   let current = obj;
   for (let i = 0; i < pathParts.length; i++) {
      if (current[pathParts[i]] !== undefined) {
         current = current[pathParts[i]];
      } else {
         return undefined;
      }
   }
   return current;
}

function ContinuousRestartsWidget({ streamName, selectedAlert, height }) {
   const ALERT_CODE = getPrimaryAlertCode(selectedAlert.codes);
   // a code the frontend does not know yet must not blank the page, so the widget degrades to the chart
   // without the code specific texts
   const { title, alertInfo, metricPath } = ALERT_MAPPER[ALERT_CODE] || {};

   // --------------------------
   // ## React useSelector hooks
   // --------------------------

   const hash = useSelector(hashSelector);
   const cancelToken = useSelector(cancelTokenSelector);
   const mode = useSelector(troubleModeSelector);

   const { details, isFetching: detailsFetching } = useAlertDetails(selectedAlert, streamName);

   // --------------------------
   // ## React useMemo hooks
   // --------------------------

   const header = useMemo(() => getHttpHeader(hash), [hash]);

   const CSV_MAX_RANGE_HOURS = 12;
   // prettier-ignore
   const FILE_NAME = `${moment.utc().format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}_alert_cr_id-${selectedAlert.id}_from-${moment.utc(selectedAlert.first).subtract(5, 'minutes').format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}_to-${moment.utc(selectedAlert.last).format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}`;

   const csvUrls = useMemo(() => {
      const start = moment.utc(selectedAlert.first).subtract(5, 'minutes');
      const end = moment.utc(selectedAlert.last);
      const base = `/v2/troubleshooting/rtmp/${metricPath}/raw/timeseries?streams=${streamName}&extendedDatapoint=true`;
      const fmt = (m) => m.format('YYYY-MM-DDTHH:mm:ss');

      const urls = [];
      let chunkStart = start.clone();
      while (chunkStart.isBefore(end)) {
         const chunkEnd = chunkStart.clone().add(CSV_MAX_RANGE_HOURS, 'hours');
         urls.push(`${base}&from=${fmt(chunkStart)}&to=${fmt(chunkEnd.isBefore(end) ? chunkEnd : end)}`);
         chunkStart = chunkEnd.isBefore(end) ? chunkEnd : end.clone();
      }
      return urls;
   }, [selectedAlert.first, selectedAlert.last, metricPath, streamName]);

   if (detailsFetching) {
      return <BlockSpinner paddingTopBottomPx={50} />;
   }

   return (
      <>
         <PaddingContainer>
            <WidgetContainer $margin={'0px 0px 0px 0px'}>
               <WidgetHeaderContainer>
                  <HeightSetContainer $heightInPx={23}>
                     <TwoSideFlexContainer>
                        <SimpleFlexRow>
                           {details?.message === 'SUCCESS' && (
                              <>
                                 <SimpleFlexItem>
                                    <CsvExportSplitApiButton
                                       urls={csvUrls}
                                       header={header}
                                       cancelToken={cancelToken}
                                       filename={FILE_NAME}
                                    />
                                 </SimpleFlexItem>
                              </>
                           )}
                        </SimpleFlexRow>
                        <SimpleFlexRow>
                           <SimpleFlexItem>
                              <AlignedIconContainer
                                 data-tooltip-id='CR'
                                 data-tooltip-html={ReactDOMServer.renderToStaticMarkup(<div>{alertInfo}</div>)}
                                 className='bi bi-info-circle ml-1'
                              ></AlignedIconContainer>
                              <ReactTooltip
                                 id='CR'
                                 place={'top'}
                                 className={'reduced-line-height bring-on-top'}
                                 backgroundColor={'rgba(0,0,0,0.9)'}
                                 textColor={'rgb(240,240,240)'}
                                 style={{ textAlign: 'center' }}
                                 clickable
                              />
                           </SimpleFlexItem>
                        </SimpleFlexRow>
                     </TwoSideFlexContainer>
                  </HeightSetContainer>
               </WidgetHeaderContainer>
               <WidgetBodyContainer>
                  <BottomMargin pxSize={20}>
                     <Row>
                        <Col>
                           <CardTitle className='mb-0'>{title}</CardTitle>
                           <div className='small text-muted'>{`From ${moment
                              .utc(selectedAlert.first)
                              .format(CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT)} to ${moment
                              .utc(selectedAlert.last)
                              .format(CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT)} \u00A0\u00A0( UTC time )`}</div>
                        </Col>
                     </Row>
                  </BottomMargin>
                  {details?.message === 'SUCCESS' ? (
                     <div>
                        <StandardContainer $paddingPx={20}>
                           <TileContainer gapPx={20} isFullScreen={mode !== E2E}>
                              {RESTART_DETAILS.map(({ name, propPath, description, round }) => (
                                 <div key={name}>
                                    <DetailTile
                                       key={propPath.split('.').at(-1)}
                                       gapPx={20}
                                       style={{ cursor: 'pointer' }}
                                       data-tooltip-id={propPath.split('.').at(-1)}
                                       data-tooltip-html={ReactDOMServer.renderToStaticMarkup(<div>{description}</div>)}
                                    >
                                       <Description marginLeftPx={0} style={{ cursor: 'pointer' }}>
                                          {name}
                                       </Description>
                                       <ValueText>
                                          {getValueFromPath(details, propPath) !== undefined ? (
                                             <>
                                                <TextWithToolTip
                                                   text={
                                                      !round
                                                         ? getValueFromPath(details, propPath)
                                                         : getValueFromPath(details, propPath).toFixed(round)
                                                   }
                                                   idName={propPath.split('.').at(-1)}
                                                />
                                             </>
                                          ) : (
                                             '-'
                                          )}
                                       </ValueText>
                                    </DetailTile>
                                    {description && (
                                       <ReactTooltip
                                          id={propPath.split('.').at(-1)}
                                          place={'top'}
                                          className={'reduced-line-height bring-on-top'}
                                          backgroundColor={'rgba(0,0,0,0.9)'}
                                          textColor={'rgb(240,240,240)'}
                                          style={{ textAlign: 'center', maxWidth: '400px' }}
                                          clickable
                                       />
                                    )}
                                 </div>
                              ))}
                           </TileContainer>
                        </StandardContainer>
                     </div>
                  ) : (
                     <CenteredMsgShowing height={height} marginTopPx={20}>
                        {'No data available'}
                        <br />
                        {details?.message}
                     </CenteredMsgShowing>
                  )}
                  <AlertDetailsPanel selectedAlert={selectedAlert} />
               </WidgetBodyContainer>
            </WidgetContainer>
         </PaddingContainer>
      </>
   );
}

ContinuousRestartsWidget.propTypes = {
   streamName: PropTypes.string,
   selectedAlert: PropTypes.object,
   height: PropTypes.number,
};

ContinuousRestartsWidget.defaultProps = {
   height: 300,
};

export default ContinuousRestartsWidget;
